(function (root, factory) {
  'use strict';

  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (root && root.document) {
    root.eSyncDeliveryEstimate = api;
    api.bootstrap(root);
  }
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';

  const numberOrNull = (value) => {
    if (value === null || value === undefined || value === '') return null;

    const number = Number(value);
    return Number.isFinite(number) ? Math.max(0, Math.trunc(number)) : null;
  };

  const positiveQuantity = (value) => {
    const quantity = Number.parseInt(value, 10);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  };

  const displayTerm = (entry) => {
    if (!entry) return null;

    const min = numberOrNull(entry.min);
    const max = numberOrNull(entry.max);
    const text = typeof entry.term === 'string' ? entry.term.trim() : '';

    if (!text && min === null && max === null) return null;

    let label = text;
    if (!label && min !== null && max !== null) {
      label = min === max ? `${min} zile` : `${min}-${max} zile`;
    } else if (!label && min !== null) {
      label = `${min} zile`;
    } else if (!label && max !== null) {
      label = `${max} zile.`;
    }

    return {
      label,
      min,
      max,
      name: typeof entry.name === 'string' ? entry.name : '',
    };
  };

  const fastestSupplier = (suppliers) => {
    const terms = (Array.isArray(suppliers) ? suppliers : [])
      .map(displayTerm)
      .filter(Boolean);
    const numericTerms = terms
      .filter((term) => term.min !== null && term.max !== null)
      .sort((left, right) => left.max - right.max || left.min - right.min);

    return numericTerms[0] || terms[0] || null;
  };

  const resolve = (variant, requestedQuantity) => {
    if (!variant) return null;

    const quantity = positiveQuantity(requestedQuantity);
    const stock = numberOrNull(variant.stock) || 0;
    const variantTerm = displayTerm(variant);
    const supplierTerm = fastestSupplier(variant.suppliers);

    if (stock > 0 && quantity <= stock && variantTerm) {
      return { ...variantTerm, source: 'stock', breakdown: '' };
    }

    if (quantity > stock && supplierTerm) {
      const overflow = quantity - stock;
      const breakdown = stock > 0 && variantTerm
        ? `${stock} buc. din stoc: ${variantTerm.label}\n${overflow} buc. aprovizionare externa: ${supplierTerm.label}`
        : '';

      return { ...supplierTerm, source: 'supplier', breakdown };
    }

    if (variantTerm) {
      return { ...variantTerm, source: 'fallback', breakdown: '' };
    }

    if (supplierTerm) {
      return { ...supplierTerm, source: 'supplier', breakdown: '' };
    }

    return null;
  };

  const largestEstimate = (estimates) => (Array.isArray(estimates) ? estimates : [])
    .filter(Boolean)
    .reduce((largest, estimate) => {
      if (!largest) return estimate;

      const largestMax = numberOrNull(largest.max) ?? numberOrNull(largest.min) ?? -1;
      const estimateMax = numberOrNull(estimate.max) ?? numberOrNull(estimate.min) ?? -1;
      if (estimateMax !== largestMax) return estimateMax > largestMax ? estimate : largest;

      const largestMin = numberOrNull(largest.min) ?? -1;
      const estimateMin = numberOrNull(estimate.min) ?? -1;
      return estimateMin > largestMin ? estimate : largest;
    }, null);

  const cartAttributes = (estimate) => ({
    _esync_delivery_term: estimate?.label || '',
    _esync_delivery_min: estimate?.min === null || estimate?.min === undefined ? '' : String(estimate.min),
    _esync_delivery_max: estimate?.max === null || estimate?.max === undefined ? '' : String(estimate.max),
  });

  const bootstrap = (windowObject) => {
    const { document } = windowObject;
    const productSelector = '[data-esync-delivery-product]';
    const cartSelector = '[data-esync-delivery-cart]';
    const dataSelector = '[data-esync-delivery-data]';
    const state = {
      started: false,
      foxThemeConnected: false,
      foxThemeTimer: null,
      subscriptions: [],
      cartAttributeTimer: null,
      cartAttributeSignature: null,
      cartAttributeRequest: null,
      pendingCartAttributes: null,
      observer: null,
    };

    const readData = (component) => {
      const node = component.querySelector(dataSelector);
      if (!node) return null;

      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        console.warn('eSYNC: datele termenului de livrare nu sunt valide.', error);
        return null;
      }
    };

    const render = (component, estimate) => {
      const term = component.querySelector('[data-esync-delivery-term]');
      const breakdown = component.querySelector('[data-esync-delivery-breakdown]');
      const hiddenClass = component.matches(productSelector)
        ? 'esync-delivery-estimate--hidden'
        : 'esync-delivery-line--hidden';

      component.classList.toggle(hiddenClass, !estimate);
      if (!estimate || !term) return;

      term.textContent = estimate.label;
      if (breakdown) {
        breakdown.textContent = estimate.breakdown || '';
        breakdown.hidden = !estimate.breakdown;
      }

      component.dispatchEvent(new windowObject.CustomEvent('esync:delivery:calculated', {
        bubbles: true,
        detail: estimate,
      }));
    };

    const productScope = (component) => component.closest('product-info') || component.closest('[data-product-id]') || document;

    const productQuantity = (component) => {
      const input = productScope(component).querySelector('quantity-input input[name="quantity"], quantity-selector input[name="quantity"], input[name="quantity"]');
      return positiveQuantity(input?.value || 1);
    };

    const productVariantId = (component) => {
      if (component.dataset.currentVariantId) return component.dataset.currentVariantId;

      const input = productScope(component).querySelector('form[is="product-form"] input[name="id"], input[name="id"]');
      return String(input?.value || component.dataset.initialVariantId || '');
    };

    const updateProduct = (component, variantId = null, quantity = null) => {
      const data = readData(component);
      if (!data?.variants) return null;

      const selectedVariantId = String(variantId || productVariantId(component));
      component.dataset.currentVariantId = selectedVariantId;
      const estimate = resolve(data.variants[selectedVariantId], quantity ?? productQuantity(component));
      render(component, estimate);
      return estimate;
    };

    const cartQuantity = (component, data) => {
      const cartItem = component.closest('.cart-item');
      const inputs = cartItem
        ? Array.from(cartItem.querySelectorAll('input[name="updates[]"]'))
        : [];
      const visibleInput = inputs.find((input) => input.offsetParent !== null) || inputs[0];

      return positiveQuantity(visibleInput?.value || data.quantity || 1);
    };

    const updateCart = (component) => {
      const data = readData(component);
      if (!data?.variant) return null;

      const estimate = resolve(data.variant, cartQuantity(component, data));
      render(component, estimate);
      return estimate;
    };

    const visibleCartComponents = () => {
      const components = Array.from(document.querySelectorAll(cartSelector));
      const visible = components.filter((component) => component.offsetParent !== null);
      return visible.length ? visible : components;
    };

    const currentCartEstimate = () => largestEstimate(
      visibleCartComponents().map((component) => {
        const data = readData(component);
        return data?.variant ? resolve(data.variant, cartQuantity(component, data)) : null;
      }),
    );

    const sendCartAttributes = (attributes) => {
      const signature = JSON.stringify(attributes);
      if (signature === state.cartAttributeSignature) {
        return state.cartAttributeRequest || Promise.resolve();
      }

      if (state.cartAttributeRequest) {
        state.pendingCartAttributes = attributes;
        return state.cartAttributeRequest;
      }

      const shopRoot = windowObject.Shopify?.routes?.root || '/';
      state.cartAttributeRequest = windowObject.fetch(`${shopRoot}cart/update.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ attributes }),
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Shopify cart/update.js: ${response.status}`);
          state.cartAttributeSignature = signature;
          return response.json();
        })
        .then((cart) => {
          document.dispatchEvent(new windowObject.CustomEvent('esync:cart-attributes:saved', {
            detail: { attributes, cart },
          }));
          return cart;
        })
        .catch((error) => {
          console.warn('eSYNC: termenul de livrare nu a putut fi salvat in cos.', error);
        })
        .finally(() => {
          state.cartAttributeRequest = null;
          const pending = state.pendingCartAttributes;
          state.pendingCartAttributes = null;

          if (pending && JSON.stringify(pending) !== state.cartAttributeSignature) {
            sendCartAttributes(pending);
          }
        });

      return state.cartAttributeRequest;
    };

    const persistCartEstimate = () => {
      state.cartAttributeTimer = null;
      if (!document.querySelector(cartSelector)) return;

      sendCartAttributes(cartAttributes(currentCartEstimate()));
    };

    const queueCartEstimate = () => {
      if (state.cartAttributeTimer) windowObject.clearTimeout(state.cartAttributeTimer);
      state.cartAttributeTimer = windowObject.setTimeout(persistCartEstimate, 150);
    };

    const hydrate = (scope = document) => {
      const productComponents = [];
      const cartComponents = [];

      if (scope instanceof windowObject.Element) {
        if (scope.matches(productSelector)) productComponents.push(scope);
        if (scope.matches(cartSelector)) cartComponents.push(scope);
      }

      scope.querySelectorAll?.(productSelector).forEach((component) => productComponents.push(component));
      scope.querySelectorAll?.(cartSelector).forEach((component) => cartComponents.push(component));

      productComponents.forEach((component) => updateProduct(component));
      cartComponents.forEach((component) => updateCart(component));
      if (cartComponents.length) queueCartEstimate();
    };

    const refreshCart = () => {
      windowObject.requestAnimationFrame(() => {
        windowObject.setTimeout(() => hydrate(), 0);
      });
    };

    const connectFoxTheme = () => {
      const pubsub = windowObject.FoxTheme?.pubsub;
      const events = pubsub?.PUB_SUB_EVENTS;
      if (!pubsub?.subscribe || !events || state.foxThemeConnected) return Boolean(state.foxThemeConnected);

      state.foxThemeConnected = true;
      state.subscriptions.push(pubsub.subscribe(events.variantChange, ({ data } = {}) => {
        if (!data?.variant?.id) return;

        document.querySelectorAll(productSelector).forEach((component) => {
          const info = component.closest('product-info');
          if (!data.sectionId || !info || String(info.dataset.section) === String(data.sectionId)) {
            updateProduct(component, data.variant.id);
          }
        });
      }));
      state.subscriptions.push(pubsub.subscribe(events.cartUpdate, refreshCart));
      return true;
    };

    const waitForFoxTheme = () => {
      if (connectFoxTheme() || state.foxThemeTimer) return;

      state.foxThemeTimer = windowObject.setInterval(() => {
        if (!connectFoxTheme()) return;

        windowObject.clearInterval(state.foxThemeTimer);
        state.foxThemeTimer = null;
      }, 50);
    };

    const delayedRefresh = (target) => {
      windowObject.setTimeout(() => {
        const productComponent = target.closest('product-info')?.querySelector(productSelector);
        if (productComponent) updateProduct(productComponent);

        const cartComponent = target.closest('.cart-item')?.querySelector(cartSelector);
        if (cartComponent) {
          updateCart(cartComponent);
          queueCartEstimate();
        }
      }, 0);
    };

    const initialize = () => {
      if (state.started) {
        hydrate();
        waitForFoxTheme();
        return;
      }

      state.started = true;
      hydrate();
      waitForFoxTheme();

      document.addEventListener('input', (event) => {
        if (event.target.matches('input[name="quantity"], input[name="updates[]"]')) {
          delayedRefresh(event.target);
        }
      });

      document.addEventListener('change', (event) => {
        if (event.target.matches('input[name="quantity"], input[name="updates[]"]')) {
          delayedRefresh(event.target);
        }
      });

      document.addEventListener('cart:updated', refreshCart);
      document.addEventListener('shopify:section:load', (event) => hydrate(event.target));
      document.addEventListener('esync:delivery:refresh', () => hydrate());

      document.addEventListener('click', (event) => {
        const openButton = event.target.closest('[data-esync-delivery-open]');
        if (openButton) {
          const dialog = openButton.closest(productSelector)?.querySelector('[data-esync-delivery-dialog]');
          if (dialog?.showModal) dialog.showModal();
          else dialog?.setAttribute('open', 'open');
          return;
        }

        const closeButton = event.target.closest('[data-esync-delivery-close]');
        if (closeButton) {
          const dialog = closeButton.closest('[data-esync-delivery-dialog]');
          if (dialog?.close) dialog.close();
          else dialog?.removeAttribute('open');
          return;
        }

        if (event.target.matches('[data-esync-delivery-dialog]')) {
          const rectangle = event.target.getBoundingClientRect();
          const outside = event.clientX < rectangle.left
            || event.clientX > rectangle.right
            || event.clientY < rectangle.top
            || event.clientY > rectangle.bottom;
          if (outside) event.target.close?.();
        }
      });

      state.observer = new windowObject.MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (!(node instanceof windowObject.Element)) return;
            if (node.matches(productSelector) || node.matches(cartSelector)) hydrate(node);
            else if (node.querySelector(`${productSelector}, ${cartSelector}`)) hydrate(node);
          });
        });
      });
      state.observer.observe(document.body, { childList: true, subtree: true });
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initialize, { once: true });
    } else {
      initialize();
    }

    api.refresh = hydrate;
  };

  return {
    numberOrNull,
    positiveQuantity,
    displayTerm,
    fastestSupplier,
    resolve,
    largestEstimate,
    cartAttributes,
    bootstrap,
    refresh: function () {},
  };
});
